import "./Contact.css";
import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import emailjs from "@emailjs/browser";
import { LanguageContext } from "../LanguageToggler/LanguageContextProvider";

const Contact = () => {
  const formRef = useRef(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);

  const { language } = useContext(LanguageContext);

  const validateEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val).toLowerCase());

  const validateForm = useCallback(() => {
    const newErrors = { name: "", email: "", message: "" };
    let valid = true;

    if (!name) {
      newErrors.name = language === "is" ? "Nafn er nauðsynlegt" : "Name is required";
      valid = false;
    }
    if (!email) {
      newErrors.email = language === "is" ? "Netfang er nauðsynlegt" : "Email is required";
      valid = false;
    } else if (!validateEmail(email)) {
      newErrors.email = language === "is" ? "Ógilt netfang" : "Invalid email address";
      valid = false;
    }
    if (!message) {
      newErrors.message = language === "is" ? "Skilaboð eru nauðsynleg" : "Message is required";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  }, [name, email, message, language]);

  useEffect(() => {
    if (Object.values(errors).some((e) => e)) {
      const newErrors = { name: "", email: "", message: "" };
      if (!name) {
        newErrors.name = language === "is" ? "Nafn er nauðsynlegt" : "Name is required";
      }
      if (!email) {
        newErrors.email = language === "is" ? "Netfang er nauðsynlegt" : "Email is required";
      } else if (!validateEmail(email)) {
        newErrors.email = language === "is" ? "Ógilt netfang" : "Invalid email address";
      }
      if (!message) {
        newErrors.message = language === "is" ? "Skilaboð eru nauðsynleg" : "Message is required";
      }
      setErrors(newErrors);
    }
    // eslint-disable-next-line
  }, [language, name, email, message]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSending(true);
    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
      );
      setStatus("success");
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
      setErrors({ name: "", email: "", message: "" });
      setTimeout(() => setStatus(""), 5000);
    } catch {
      setStatus("error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-container w-100">
      {language === "is" ? (
        <h4 className="head4">Hafa samband</h4>
      ) : (
        <h4 className="head4">Contact</h4>
      )}

      <form ref={formRef} onSubmit={handleSubmit}>
        <div className="mb-2 m-auto">
          <label className="form-label">
            {language === "is" ? "Nafn" : "Name"}
          </label>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            type="text"
            name="name"
            className="form-control no-border-radius-input"
          />
          {errors.name && (
            <div style={{ color: "#ff6666", fontSize: "0.8rem", marginTop: "0.1rem" }}>
              {errors.name}
            </div>
          )}
        </div>

        <div className="mb-2 m-auto">
          <label className="form-label">
            {language === "is" ? "Netfang" : "Email"}
          </label>
          <input
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            name="email"
            className="form-control no-border-radius-input"
          />
          {errors.email && (
            <div style={{ color: "#ff6666", fontSize: "0.8rem", marginTop: "0.1rem" }}>
              {errors.email}
            </div>
          )}
        </div>

        <div className="mb-2 m-auto">
          <label className="form-label">
            {language === "is" ? "Símanúmer" : "Phone"}{" "}
            <span style={{ opacity: 0.6, fontSize: "0.85em" }}>
              ({language === "is" ? "valfrjálst" : "optional"})
            </span>
          </label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            type="tel"
            name="phone"
            className="form-control no-border-radius-input"
          />
        </div>

        <div className="mb-2 m-auto">
          <label className="form-label">
            {language === "is" ? "Skilaboð" : "Message"}
          </label>
          <textarea
            required
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            name="message"
            className="form-control no-border-radius-input"
            rows="3"
          />
          {errors.message && (
            <div style={{ color: "#ff6666", fontSize: "0.8rem", marginTop: "0.1rem" }}>
              {errors.message}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={sending}
          className="btn btn-primary btn-no-radius w-100 mt-2"
        >
          {sending
            ? (language === "is" ? "Sendi..." : "Sending...")
            : (language === "is" ? "Senda skilaboð" : "Send message")}
        </button>

        {status === "success" && (
          <div className="mt-3 alert alert-success">
            {language === "is" ? "Skilaboð send!" : "Message sent successfully!"}
          </div>
        )}
        {status === "error" && (
          <div className="mt-3 alert alert-danger">
            {language === "is" ? "Villa kom upp. Reyndu aftur." : "There was an error. Please try again."}
          </div>
        )}
      </form>
    </div>
  );
};

export default Contact;
